import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, Typography
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, ComposedChart,
  Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import { fetchActivityData } from "../../../services/dashboardModule/dashboardServices.js";
import { fetchPtpAging } from "../../../services/scheduleModule/ptpResponseServices.js";
// Accounts comes from the SAME endpoint the Scorecard, Trends and PTP pages
// use, so "Accounts" means one thing everywhere: distinct Accountid in
// customerconvevent over the window.
import { getDashboardAccountsHourly as getAccounts }
  from "../../../services/pbi/pbiservices.js";
import DatePickerComponent from "../../../common/components/DatePickerComponent.jsx";
import { STRINGS } from "../../../common/constants/commonConstants.js";
import { formatDateTime } from "../../../common/constants/dashboardModule/activityConstants.js";

// ── THE NAMING, WRITTEN DOWN ───────────────────────────────────────
//
//   Response %   Responses / Sent       did the message land
//   RPC %        Responses / Accounts   did we reach the account
//
// The SAME COUNT appears twice under two labels — "Responses" in Reach &
// totals, "RPC" in PTP performance — because deliverability and conversion are
// different questions and need different bases. Same arrangement as the PTP
// Responses page.
//
//   PTP     Kept + Promises      everything committed to
//   PTP %   PTP / RPC            of those reached, how many committed
//   Kept %  Kept / PTP           of what was committed, how much arrived
//
// ⚠ UNCOMMITTED IS NEITHER SUMMED NOR SHOWN. It is customers who were given
// the payment method and never selected one — it commits to nothing, so it
// adds to nothing and appears nowhere. `Promises` here is the SCHEDULED half
// (CF-AI4-PTP), which does feed PTP; it just has no display of its own.
//   SL %    SmartLinks / RPC     a smart link is its own event, not part of PTP
//
// ⚠ THIS PAGE HAS NO RESPONSE COUNT OF ITS OWN. /getactivity returns
// CustomersContacted and no responses field, and every conversion rate here
// already divides by CustomersContacted — that is the RPC role, so it is
// mapped to RPC.
//
// IF CustomersContacted MEANS CUSTOMERS WE MESSAGED rather than customers who
// replied, that mapping is wrong: it would be Accounts, this page would have
// no RPC at all, and the getAccounts call below should be deleted rather than
// added. Worth settling against the route before anyone reads these rates.
//
// ⚠ Response % cannot be shown here either way — there is no Sent column. The
// Responses tile carries a count and no rate.
//
// ── THE PALETTE IS THE REPORT'S ──
// rpt1's PDF is the artifact people forward: RPC blue, PTP green, Kept a
// darker shade of that green, Accounts brown.
const shade = (hex, pct) => {
  const n = parseInt(String(hex).slice(1), 16);
  const f = (c) => Math.max(0, Math.min(255, Math.round(c + c * pct)));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255]
    .map(f).map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};
const KEPT_SHADE = -0.42;

const C_ACCOUNTS    = "#8A5524";  // brown
const C_RPC         = "#378ADD";  // blue
const C_PTP         = "#1D9E75";  // green
const C_SL          = "#D4537E";  // pink
const C_KEPT        = shade(C_PTP, KEPT_SHADE);   // dark green

// Chrome and non-metric accents.
const BLUE   = "#378ADD";
const AMBER  = "#BA7517";
const PURPLE = "#7F77DD";

// ── Helpers ────────────────────────────────────────────────────────
function fmtCurrency(n) {
  const num = Number(n);
  if (!n || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1000000) return `$${(num / 1000000).toFixed(2)}M`;
  if (Math.abs(num) >= 1000)    return `$${(num / 1000).toFixed(2)}K`;
  return `$${num.toFixed(2)}`;
}

function fmtDate(val) {
  if (!val) return "";
  // Parse YYYY-MM-DD as a local date — not UTC. `new Date("2026-05-08")`
  // and `new Date("2026-05-08T00:00:00.000Z")` both give UTC midnight,
  // which lands on the previous day in negative-offset timezones.
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) {
      const d = new Date(+m[1], +m[2] - 1, +m[3]);
      if (!isNaN(d)) {
        return d.toLocaleDateString("en-US", {
          month: "2-digit", day: "2-digit", year: "2-digit",
        });
      }
    }
  }
  const d = new Date(val);
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

// Serialised from the date parts — toISOString() converts to UTC first, which
// shifts the date by a day in negative-offset zones. The accounts route wants
// YYYY-MM-DD; formatDateTime keeps serving the calls it already served.
function toLocalYMD(d) {
  if (!(d instanceof Date) || isNaN(d)) return "";
  const yy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mo}-${dy}`;
}

function safeNum(v) {
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

// ── Sub-components ─────────────────────────────────────────────────
function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

// KPI tile — three stacked rows with the PERCENTAGE as the hero
// (big, accent-colored), the $ amount in the middle, and the raw count
// as a small footer in the same accent color.
function KpiTile({ label, count, amount, percent, color = BLUE }) {
  const hasPercent = percent !== null && percent !== undefined;
  const hasCount   = count   !== null && count   !== undefined;
  const hasAmount  = amount  !== null && amount  !== undefined;

  let heroValue;
  let heroIsAmount = false;
  if (hasPercent)      heroValue = percent;
  else if (hasCount)   heroValue = count;
  else if (hasAmount)  { heroValue = amount; heroIsAmount = true; }
  else                 heroValue = "—";

  const heroColor = (hasPercent || hasCount || hasAmount) ? color : "text.disabled";
  const showAmountRow = hasAmount && !heroIsAmount;
  const showFooterCount = hasCount && hasPercent;

  return (
    <Box sx={{
      background: "#f7f7f7", borderRadius: 2,
      p: "10px 12px",
      flex: 1, minWidth: 0,
      display: "flex", flexDirection: "column", gap: "6px",
    }}>
      <Typography sx={{
        fontSize: 10, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em",
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {label}
      </Typography>

      <Box sx={{ minHeight: 26 }}>
        <Typography sx={{
          fontSize: 20, fontWeight: 500, color: heroColor, lineHeight: 1.2,
        }}>
          {heroValue}
        </Typography>
      </Box>

      <Box sx={{ minHeight: 22 }}>
        {showAmountRow ? (
          <Typography sx={{
            fontSize: 15, fontWeight: 500, color: "text.primary", lineHeight: 1.2,
          }}>
            {amount}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: 15, color: "text.disabled", lineHeight: 1.2,
          }}>
            —
          </Typography>
        )}
      </Box>

      <Box sx={{ minHeight: 18 }}>
        {showFooterCount ? (
          <Typography sx={{
            fontSize: 12, fontWeight: 500, color, lineHeight: 1.2,
          }}>
            {count}
          </Typography>
        ) : (
          <Typography sx={{
            fontSize: 12, color: "text.disabled", lineHeight: 1.2,
          }}>
            —
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function ChartCard({ title, children, height = 200 }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      {title && (
        <Typography sx={{
          fontSize: 11, fontWeight: 500, color: "text.secondary",
          textTransform: "uppercase", letterSpacing: "0.05em", mb: 1,
        }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── PTP aging row ──────────────────────────────────────────────────
// Row of KPI tiles showing aging buckets for the underlying PTP events.
//
//   Buckets (exclusive — not cumulative):
//     1d = 0–24 hrs, 2d = 24–48, … 7d = 144–168, 7+d = 168+
//     Total = sum of all buckets (sanity check)
//
//   Aging = DATEDIFF(hour, create_dt, EventTime).
//
// ── IT HAS TO COVER BOTH EVENT CODES ──
// PTP is Kept + Promises, so "time to pay" is only the whole story if the
// buckets count CF-AI3-PAY (Kept / sameday pay) as well as CF-AI4-PTP
// (Promises / future-proc). Sameday events age at ~0 hours, so they land
// almost entirely in the 1-day bucket; future-proc spreads across 2d–7d+.
//
// ⚠ WHETHER THEY DO IS A SERVER-SIDE FACT THIS COMPONENT CANNOT CHECK. The
// route hands back one `committed` metric and the client cannot see which
// event codes went into it. So the row checks itself: the 1-day bucket should
// be at or above the Kept count for the same range. If it is materially below,
// /dashboard/ptp-aging is filtering to CF-AI4-PTP only and the fix is on the
// route. A warning renders naming both figures rather than leaving a quietly
// understated row.
//
//   Unscheduled PTP is NOT rendered here — the upstream Cosmos docs feeding
//   staging_ptp_future* don't capture the scheduled future pay date, so the
//   "scheduled − made" delta isn't computable.
//
// Empty data → row is hidden (rather than showing 9 zeros).
function PtpAgingRows({ startDate, endDate, channel, keptCount }) {
  const [aging,   setAging]   = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!startDate || !endDate) return;

    setLoading(true);

    // The service layer handles base URL, auth, and the GET wrapping;
    // we just pass formatted dates and the channel/campaign filters.
    fetchPtpAging({
      startDate: formatDateTime(startDate),
      endDate:   formatDateTime(endDate),
      channel:   String(channel || "texts").toLowerCase(),
    })
      .then(d => { if (!cancelled) setAging(d); })
      .catch(() => { if (!cancelled) setAging(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [startDate, endDate, channel]);

  if (loading || !aging) return null;

  const renderRow = (title, metric, accent) => {
    const buckets = metric?.buckets || [];
    const total   = metric?.total   || { count: 0, amount: 0 };
    if (total.count === 0) return null;

    const oneDay  = Number(buckets[0]?.count) || 0;
    const missing = keptCount > 0 && oneDay + 1 < keptCount;

    return (
      <>
        <SectionTitle>{title}</SectionTitle>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: missing ? 0.75 : 1.5 }}>
          {buckets.map(b => (
            <KpiTile
              key={b.bucket}
              label={b.label}
              count={Number(b.count).toLocaleString()}
              amount={fmtCurrency(b.amount)}
              percent={`${Number(b.pct).toFixed(1)}%`}
              color={accent}
            />
          ))}
          <KpiTile
            label="Total"
            count={total.count.toLocaleString()}
            amount={fmtCurrency(total.amount)}
            percent={null}
            color={accent}
          />
        </Box>
        {missing && (
          <Typography sx={{ fontSize: 11, color: C_SL, mb: 1.5, lineHeight: 1.5 }}>
            ⚠ The 1-day bucket holds {oneDay.toLocaleString()} events but Same Day is{" "}
            {keptCount.toLocaleString()} for the same range. Same-day payments land in
            the 1-day bucket, so this row is probably counting future-proc events only —
            check whether /dashboard/ptp-aging filters to CF-AI4-PTP.
          </Typography>
        )}
      </>
    );
  };

  return (
    <>
      {renderRow("PTP — time to pay (Same Day + Promises)", aging.committed, C_PTP)}
    </>
  );
}

// ── Main Component ─────────────────────────────────────────────────
const Payments = () => {
  const [startDate,    setStartDate]    = useState(() => new Date());
  const [endDate,      setEndDate]      = useState(() => new Date());
  const [activityType, setActivityType] = useState("Texts");
  const [data,         setData]         = useState([]);
  // Accounts worked in the window. Its own endpoint — the distinct scan is too
  // costly to ride along on the activity read.
  const [accounts,     setAccounts]     = useState(0);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");

  const load = useCallback(async (start, end, type) => {
    setLoading(true);
    setError("");
    try {
      // Accounts failure is swallowed: it costs one tile and one rate, where
      // letting it throw would cost the whole page.
      const [result, acc] = await Promise.all([
        fetchActivityData({
          startDate:    formatDateTime(start),
          endDate:      formatDateTime(end),
          activityType: type,
        }),
        getAccounts({
          startDate:   toLocalYMD(start),
          endDate:     toLocalYMD(end),
          granularity: "day",
          channel:     String(type || "texts").toLowerCase(),
        }).catch(() => ({})),
      ]);
      setData(result || []);
      setAccounts(safeNum(acc?.account_count));
    } catch (e) {
      console.error(e);
      setError("Failed to load activity data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(startDate, endDate, "Texts");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kpis = useMemo(() => {
    const keptAmt = data.reduce((a,b) => a + safeNum(b.PaymentAmount),   0);
    const uncAmt  = data.reduce((a,b) => a + safeNum(b.PromiseAmount),   0);
    const slAmt   = data.reduce((a,b) => a + safeNum(b.SmartLinkAmount), 0);
    const kept    = data.reduce((a,b) => a + safeNum(b.Payments),   0);
    const uncomm  = data.reduce((a,b) => a + safeNum(b.Promises),   0);
    const sl      = data.reduce((a,b) => a + safeNum(b.SmartLinks), 0);
    // See the ⚠ at the top: this is the RPC role on this page.
    const rpc     = data.reduce((a,b) => a + safeNum(b.CustomersContacted), 0);

    // PTP is Kept + Promises. Smart links are NOT in it — a smart link is its
    // own event code, which is why SL reads against RPC rather than against
    // PTP.
    const ptpCount = kept + uncomm;
    const ptpAmt   = keptAmt + uncAmt;

    const pctRpc = n => rpc ? (n / rpc) * 100 : 0;

    return {
      rpc, accounts,
      kept, uncomm, sl,
      keptAmt, uncAmt, slAmt,
      ptpCount, ptpAmt,
      totalCount: kept + uncomm + sl,
      totalAmt:   keptAmt + uncAmt + slAmt,
      // Responses / Accounts — reach.
      rpcPct:      accounts ? (rpc / accounts) * 100 : 0,
      // PTP / RPC — of those reached, how many committed.
      ptpPct:      pctRpc(ptpCount),
      // Kept / PTP — of what was committed, how much arrived. Counts and $
      // both, since this page leads its money tiles with dollars.
      keptPct:     ptpCount ? (kept    / ptpCount) * 100 : 0,
      keptAmtPct:  ptpAmt   ? (keptAmt / ptpAmt)   * 100 : 0,
      slPct:       pctRpc(sl),
      totalPct:    pctRpc(kept + uncomm + sl),
    };
  }, [data, accounts]);

  const chartData = useMemo(() => {
    const map = {};
    data.forEach(r => {
      const key = fmtDate(r.ActivityDate);
      if (!map[key]) map[key] = {
        date: key,
        kept: 0, keptAmt: 0,
        uncomm: 0, uncAmt: 0,
        sl: 0, slAmt: 0,
        rpc: 0,
      };
      map[key].kept    += safeNum(r.Payments);
      map[key].keptAmt += safeNum(r.PaymentAmount);
      map[key].uncomm  += safeNum(r.Promises);
      map[key].uncAmt  += safeNum(r.PromiseAmount);
      map[key].sl      += safeNum(r.SmartLinks);
      map[key].slAmt   += safeNum(r.SmartLinkAmount);
      map[key].rpc     += safeNum(r.CustomersContacted);
    });
    // PTP derived per bucket, so the chart plots the same total the tile does.
    return Object.values(map)
      .map(m => ({ ...m, ptp: m.kept + m.uncomm, ptpAmt: m.keptAmt + m.uncAmt }))
      .sort((a,b) => new Date(a.date) - new Date(b.date));
  }, [data]);

  const axisStyle    = { fontSize: 10, fill: "#888" };
  const tooltipStyle = { fontSize: 12 };

  // "RPC" rather than "Contacted": the column is the count every rate on this
  // page divides by, which is the RPC role. The promise half has no column:
  // it feeds PTP and is PTP minus Kept for anyone who needs it.
  const COLS = "100px 60px 90px 90px 75px 80px 110px 100px 100px 100px";
  const HEADERS = [
    "Date", "DPD", "RPC",
    "PTP", "Same Day", "Smart Links",
    "PTP $", "Same Day $", "SL $", "Total $",
  ];

  const displayedRows = useMemo(() => data.slice(0, 100), [data]);

  const visibleTotals = useMemo(() => {
    const keptAmt = displayedRows.reduce((a,b) => a + safeNum(b.PaymentAmount),   0);
    const uncAmt  = displayedRows.reduce((a,b) => a + safeNum(b.PromiseAmount),   0);
    const slAmt   = displayedRows.reduce((a,b) => a + safeNum(b.SmartLinkAmount), 0);
    const kept    = displayedRows.reduce((a,b) => a + safeNum(b.Payments),   0);
    const uncomm  = displayedRows.reduce((a,b) => a + safeNum(b.Promises),   0);
    const sl      = displayedRows.reduce((a,b) => a + safeNum(b.SmartLinks), 0);
    return {
      rpc: displayedRows.reduce((a,b) => a + safeNum(b.CustomersContacted), 0),
      kept, uncomm, sl,
      keptAmt, uncAmt, slAmt,
      ptpCount: kept + uncomm,
      ptpAmt:   keptAmt + uncAmt,
      totalAmt: keptAmt + uncAmt + slAmt,
    };
  }, [displayedRows]);

  return (
    <Box sx={{ p: 2 }}>

      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2,
        alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <DatePickerComponent
          label={STRINGS.START_DATE}
          date={startDate}
          onDateChange={setStartDate}
          marginRight={0}
        />
        <DatePickerComponent
          label={STRINGS.END_DATE}
          date={endDate}
          onDateChange={d => { if (d >= startDate) setEndDate(d); }}
          marginRight={0}
        />
        <Box
          onClick={() => load(startDate, endDate, activityType)}
          sx={{
            px: "14px", py: "6px", fontSize: 13, borderRadius: "20px",
            cursor: "pointer", userSelect: "none",
            background: BLUE, color: "#fff", fontWeight: 500,
            alignSelf: "flex-end", mb: "1px",
            "&:hover": { background: "#2a6db5" },
          }}
        >
          Search
        </Box>

        <Box sx={{
          display: "flex", ml: "auto",
          border: "0.5px solid", borderColor: "divider",
          borderRadius: "20px", overflow: "hidden",
          alignSelf: "flex-end",
        }}>
          {["Texts", "Emails"].map(ch => (
            <Box key={ch} onClick={() => {
              setActivityType(ch);
              load(startDate, endDate, ch);
            }} sx={{
              px: "14px", py: "5px", fontSize: 13,
              fontWeight:  activityType === ch ? 500 : 400,
              cursor: "pointer", userSelect: "none",
              background:  activityType === ch ? BLUE : "transparent",
              color:       activityType === ch ? "#fff" : "text.secondary",
              transition:  "all .15s",
            }}>
              {ch}
            </Box>
          ))}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 3 }}>
          <CircularProgress size={20} /> Loading…
        </Box>
      )}

      {!loading && data.length === 0 && !error && (
        <Typography sx={{ p: 3, color: "text.secondary", textAlign: "center" }}>
          No data for selected range.
        </Typography>
      )}

      {!loading && data.length > 0 && (
        <>
          {/* ── PTP PERFORMANCE: THE CONVERSION CHAIN, IN ORDER ──
              Accounts → RPC → PTP → Kept, each rate against the step before
              it, then Smart Links which hangs off RPC rather than off PTP.
              Accounts carries no rate: it is the base the chain starts from.

              The promise half has no tile and no column. It still feeds PTP —
              the total needs both halves — but it is never displayed on its
              own, and Uncommitted is not in PTP at all. */}
          <SectionTitle>PTP performance</SectionTitle>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <KpiTile label="Accounts"
              count={accounts ? accounts.toLocaleString() : "—"}
              amount={null} percent={null} color={C_ACCOUNTS} />
            <KpiTile label="RPC"
              percent={accounts ? `${kpis.rpcPct.toFixed(2)}%` : null}
              amount={null}
              count={kpis.rpc.toLocaleString()}
              color={C_RPC} />
            <KpiTile label="PTP"
              percent={`${kpis.ptpPct.toFixed(2)}%`}
              amount={fmtCurrency(kpis.ptpAmt)}
              count={kpis.ptpCount.toLocaleString()}
              color={C_PTP} />
            <KpiTile label="Same Day"
              percent={`${kpis.keptPct.toFixed(2)}%`}
              amount={fmtCurrency(kpis.keptAmt)}
              count={kpis.kept.toLocaleString()}
              color={C_KEPT} />
            <KpiTile label="Smart Links"
              percent={`${kpis.slPct.toFixed(2)}%`}
              amount={fmtCurrency(kpis.slAmt)}
              count={kpis.sl.toLocaleString()}
              color={C_SL} />
          </Box>

          <Typography sx={{
            fontSize: 11, color: "text.disabled", mb: 2, lineHeight: 1.5,
          }}>
            <b>RPC %</b> is responses over accounts. <b>PTP</b> is Same Day + Promises read
            against RPC, <b>Same Day %</b> is Same Day over PTP, and <b>SL %</b> is smart links
            over RPC — a smart link is its own event, not a part of PTP. The{" "}
            <b>Responses</b> tile below is the same count as RPC; it carries no rate
            because this page has no send count to measure it against.
          </Typography>

          <SectionTitle>Reach &amp; totals</SectionTitle>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <KpiTile label="Responses"
              count={kpis.rpc.toLocaleString()}
              amount={null} percent={null} color={C_RPC} />
            <KpiTile label="Same Day $ %"
              percent={`${kpis.keptAmtPct.toFixed(2)}%`}
              amount={fmtCurrency(kpis.keptAmt)}
              count={fmtCurrency(kpis.ptpAmt)}
              color={C_KEPT} />
            <KpiTile label="Total"
              percent={`${kpis.totalPct.toFixed(2)}%`}
              amount={fmtCurrency(kpis.totalAmt)}
              count={kpis.totalCount.toLocaleString()}
              color={AMBER} />
          </Box>

          {/* ── AGING ROWS ────────────────────────────────────── */}
          {/* keptCount is passed in so the row can check itself — same-day
              payments belong in the 1-day bucket, so a 1d count below Kept
              means the route is missing CF-AI3-PAY. */}
          <PtpAgingRows
            startDate={startDate}
            endDate={endDate}
            channel={activityType}
            keptCount={kpis.kept}
          />

          <SectionTitle>Amount over time</SectionTitle>

          <ChartCard height={240}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis yAxisId="left"  tick={axisStyle} tickFormatter={v => fmtCurrency(v)} />
                <YAxis yAxisId="right" orientation="right" tick={{ ...axisStyle, fill: C_RPC }} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(v, n) =>
                    n === "RPC" ? [v, n] : [fmtCurrency(v), n]
                  }
                />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                {/* GROUPED, not stacked. PTP CONTAINS Kept, so stacking the
                    two would draw the same money twice — the pair has to sit
                    side by side and be read as whole against part. Smart
                    links are outside PTP entirely. */}
                <Bar yAxisId="left" dataKey="ptpAmt"  name="PTP $"       fill={C_PTP}  radius={[3,3,0,0]} />
                <Bar yAxisId="left" dataKey="keptAmt" name="Same Day $"  fill={C_KEPT} radius={[3,3,0,0]} />
                <Bar yAxisId="left" dataKey="slAmt"   name="SmartLink $" fill={C_SL}   radius={[3,3,0,0]} />
                <Line yAxisId="right" type="monotone" dataKey="rpc" name="RPC"
                  stroke={C_RPC} dot={{ r: 3 }} strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartCard>

          <SectionTitle>PTP vs Same Day vs smart links</SectionTitle>

          <ChartCard height={200}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                <XAxis dataKey="date" tick={axisStyle} />
                <YAxis tick={axisStyle} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="ptp"  name="PTP"         fill={C_PTP}  radius={[3,3,0,0]} />
                <Bar dataKey="kept" name="Same Day"    fill={C_KEPT} radius={[3,3,0,0]} />
                <Bar dataKey="sl"   name="Smart Links" fill={C_SL}   radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <SectionTitle>Detail</SectionTitle>

          <Box sx={{
            border: "0.5px solid", borderColor: "divider",
            borderRadius: 2, overflow: "auto",
          }}>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              background: "#f7f7f7",
              borderBottom: "0.5px solid", borderColor: "divider",
              px: 2, py: 1,
              fontSize: 11, fontWeight: 500,
              color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em",
            }}>
              {HEADERS.map((h, i) => (
                <Box key={h} sx={{ textAlign: i === 0 ? "left" : "right" }}>{h}</Box>
              ))}
            </Box>

            {displayedRows.map((row, i) => {
              const kept    = safeNum(row.Payments);
              const uncomm  = safeNum(row.Promises);
              const sl      = safeNum(row.SmartLinks);
              const keptAmt = safeNum(row.PaymentAmount);
              const uncAmt  = safeNum(row.PromiseAmount);
              const slAmt   = safeNum(row.SmartLinkAmount);
              // PTP = Kept + Promises, derived per row so the table carries
              // the same total the tile does.
              const ptpCount = kept + uncomm;
              const ptpAmt   = keptAmt + uncAmt;
              return (
                <Box key={i} sx={{
                  display: "grid",
                  gridTemplateColumns: COLS,
                  px: 2, py: "6px",
                  fontSize: 12,
                  borderBottom: "0.5px solid", borderColor: "divider",
                  background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
                  "&:hover": { background: "rgba(55,138,221,0.06)" },
                }}>
                  <Box>{fmtDate(row.ActivityDate)}</Box>
                  <Box sx={{ textAlign: "right" }}>{row.DPD ?? "—"}</Box>
                  <Box sx={{ textAlign: "right", color: C_RPC, fontWeight: 500 }}>
                    {safeNum(row.CustomersContacted).toLocaleString()}
                  </Box>
                  <Box sx={{ textAlign: "right", color: C_PTP,         fontWeight: 500 }}>{ptpCount}</Box>
                  <Box sx={{ textAlign: "right", color: C_KEPT,        fontWeight: 500 }}>{kept}</Box>
                  <Box sx={{ textAlign: "right", color: C_SL,          fontWeight: 500 }}>{sl}</Box>
                  <Box sx={{ textAlign: "right", color: C_PTP,  fontWeight: 500 }}>{fmtCurrency(ptpAmt)}</Box>
                  <Box sx={{ textAlign: "right", color: C_KEPT }}>{fmtCurrency(keptAmt)}</Box>
                  <Box sx={{ textAlign: "right", color: C_SL }}>{fmtCurrency(slAmt)}</Box>
                  <Box sx={{ textAlign: "right", fontWeight: 500 }}>
                    {fmtCurrency(keptAmt + uncAmt + slAmt)}
                  </Box>
                </Box>
              );
            })}

            <Box sx={{
              display: "grid",
              gridTemplateColumns: COLS,
              px: 2, py: "8px",
              fontSize: 12, fontWeight: 600,
              borderTop: "1px solid", borderColor: "divider",
              background: "#f0f3f7",
            }}>
              <Box>Total</Box>
              <Box sx={{ textAlign: "right" }}>—</Box>
              <Box sx={{ textAlign: "right", color: C_RPC }}>{visibleTotals.rpc.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_PTP         }}>{visibleTotals.ptpCount.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_KEPT        }}>{visibleTotals.kept.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_SL          }}>{visibleTotals.sl.toLocaleString()}</Box>
              <Box sx={{ textAlign: "right", color: C_PTP  }}>{fmtCurrency(visibleTotals.ptpAmt)}</Box>
              <Box sx={{ textAlign: "right", color: C_KEPT }}>{fmtCurrency(visibleTotals.keptAmt)}</Box>
              <Box sx={{ textAlign: "right", color: C_SL }}>{fmtCurrency(visibleTotals.slAmt)}</Box>
              <Box sx={{ textAlign: "right" }}>{fmtCurrency(visibleTotals.totalAmt)}</Box>
            </Box>
          </Box>

          {data.length > displayedRows.length && (
            <Typography sx={{
              fontSize: 11, color: "text.secondary",
              mt: 1, textAlign: "right",
            }}>
              Showing {displayedRows.length.toLocaleString()} of {data.length.toLocaleString()} rows.
              KPI cards above reflect the full range.
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default Payments;